import Keycloak from 'keycloak-js';
import { useEffect, useCallback, useRef } from 'react';
import {
    UserProfile,
} from '../Orchestrator/Recoils/atoms';
import { getSession, setSession } from '../Lib/Utils/SessionStorageUtil';
import { getRuntimeConfig } from "../Lib/Config/RuntimeConfig";
import {
    updateRecoilState,
    useRecState,
    suppressLocalStorageWrites,
    purgeAllRecoilStateFromLocalStorage,
} from '../Orchestrator/Recoils/Events/Recoil';
import { _eventBus } from '../core/events/EventBus';
import { apiConfigManager } from '../Lib/Utils/ApiConfigManager';
import { cacheManager } from '../Lib/Config/CacheManager';
import { FlowPersistence } from '../core/flows/FlowPersistence';
import { Analytics } from "../Lib/Analytics/events";
import { sha256 } from "../Lib/Analytics/userId";
import type { RegisterMethod } from "../Lib/Analytics/events";

// Emit event when token is ready
export const emitTokenReadyEvent = () => {
    _eventBus.emit({
        eventName: 'TOKEN_READY'
    });
};

export const useAuth = () => {
    const [initialized] = useRecState<boolean>("initializedStateAtom");
    const [isAuthCheckComplete] = useRecState<boolean>("isAuthCheckCompleteStateAtom");
    const [isAuthenticated] = useRecState<boolean>("isAuthenticatedStateAtom");
    const [isAnonymous] = useRecState<boolean>("isAnonymousStateAtom");
    const [token] = useRecState<string>("tokenStateAtom");
    const [userProfile] = useRecState<UserProfile>("userProfileStateAtom");
    const [authLoaded] = useRecState<boolean>("authLoadedStateAtom");

    const keycloakRef = useRef<Keycloak | null>(null);
    const isInitializingRef = useRef<boolean>(false);
    const isLoggingOutRef = useRef<boolean>(false);

    // Function to handle logout
    const logout = useCallback(async () => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        const redirectUri = `${window.location.origin}`;
        const kc = keycloakRef.current;
        // Capture tokens from the in-memory Keycloak instance BEFORE storage is purged.
        const accessToken = kc?.token;
        const idTokenHint = kc?.idToken;

        // ------------------------------------------------------------------
        // 1. Prevent any microtask-scheduled UPDATE_REC_STATE handlers from
        //    re-writing atoms to localStorage after we clear it.
        // ------------------------------------------------------------------
        suppressLocalStorageWrites();

        // ------------------------------------------------------------------
        // 2. Synchronously clear ALL persisted state so nothing survives the
        //    redirect.  Order does not matter here because we suppress writes
        //    above and we will navigate away at the end.
        // ------------------------------------------------------------------
        try {
            // Clear every recoil_state_* key (broader than the whitelist)
            purgeAllRecoilStateFromLocalStorage();

            // Caches and flow persistence
            cacheManager.clearAll();
            apiConfigManager.clearStoredConfig();
            localStorage.removeItem("resumeSearch");
            FlowPersistence.cleanup();

            // Set the logging out flag (checked by page components to prevent spurious login redirects)
            sessionStorage.setItem("isLoggingOut", "true");
            // Remove specific keys instead of clear() to preserve isLoggingOut
            const keysToRemove: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key !== "isLoggingOut") {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
        } catch (err) {
            console.error("[useAuth] Error during logout cleanup:", err);
        }

        // ------------------------------------------------------------------
        // 2b. End the Transcity customer session server-side (customers/logout)
        //     BEFORE redirecting to Keycloak. Best-effort + timed out so a slow
        //     or failing backend never traps the user on logout.
        // ------------------------------------------------------------------
        if (accessToken && idTokenHint) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 4000);
                await fetch(
                    `/api/logout?redirectUri=${encodeURIComponent(redirectUri)}&idTokenHint=${encodeURIComponent(idTokenHint)}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": "application/json",
                        },
                        signal: controller.signal,
                    }
                );
                clearTimeout(timeoutId);
            } catch (err) {
                console.error("[useAuth] customers/logout failed (continuing to Keycloak logout):", err);
            }
        }

        // ------------------------------------------------------------------
        // 3. Redirect the BROWSER to the Keycloak logout endpoint.
        //    This is critical: the browser must visit the Keycloak URL so
        //    that Keycloak can clear its session cookie via Set-Cookie in
        //    the response.  A server-side fetch does NOT clear the cookie.
        // ------------------------------------------------------------------
        if (kc) {
            try {
                // keycloak-js builds the correct end_session_endpoint URL
                // including the id_token_hint from the current session.
                kc.logout({ redirectUri });
                // kc.logout sets window.location internally; execution
                // continues until the browser navigates, but nothing
                // below this line should rely on client state.
            } catch (err) {
                console.error("[useAuth] Keycloak logout failed, falling back to redirect:", err);
                window.location.href = redirectUri;
            }
        } else {
            // Keycloak was never initialized -- just go home.
            window.location.href = redirectUri;
        }

        keycloakRef.current = null;
    }, []);

    // Function to handle login
    const login = useCallback(() => {
        if (keycloakRef.current) {
            let redirectTo = "/search"
            if (sessionStorage.getItem("redirectAfterLogin")) {
                redirectTo = sessionStorage.getItem("redirectAfterLogin") || "/search"
            }

            let url = new URL(redirectTo, window.location.origin);
            url.searchParams.set("successLogin", "true");
            url.searchParams.set("kc_locale", sessionStorage.getItem("lang")?.toLowerCase() || "en");
            redirectTo = url.toString();

            // #1 keyclock_register_email — closest in-app signal to "submits
            // Create account form" (the form lives on Keycloak's hosted pages).
            Analytics.registerEmailAttempt();

            keycloakRef.current.login({ redirectUri: redirectTo }).catch((error: any) => {
                console.error('Login error:', error);
            });
        } else {
            console.error('Keycloak is not initialized yet.');
        }
    }, []);

    const saveRefreshTokenToCookie = (refresh_token: string) => {
        // make API call to /api/refresh-token/save with post method to store refresh token in cookie
        return fetch('/api/token/refresh-token/save', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token }),
        });
    };

    // Function to update session after successful login
    const updateSessionAfterLogin = useCallback(
        async (kc: Keycloak) => {
            const currentToken = kc.token;
            const currentIdTokenHint = kc.idToken;
            const currentRefreshToken = kc.refreshToken;

            if (currentToken && currentRefreshToken && currentIdTokenHint) {
                /**
                 * Removed retrieve booking cash data if any
                 * Removed from local storage.
                 * @rb means Retrieve Booking
                 * -------- Start ---------
                 */
                try {
                    const rbCacheData = localStorage.getItem("retrieveBookingCacheData")
                    if (rbCacheData) {
                        localStorage.removeItem("retrieveBookingCacheData")
                    }
                } catch (error) {
                    console.log("Error: ", error)
                }
                /*-------- End ---------*/

                let user: UserProfile;
                // Only load the user profile if it hasn't been loaded already.
                if (!userProfile) {
                    try {
                        const profile = await kc.loadUserProfile();
                        user = {
                            id: profile.id!,
                            username: profile.username!,
                            email: profile.email!,
                            firstName: profile.firstName!,
                            lastName: profile.lastName!,
                        };
                        updateRecoilState("userProfileStateAtom", user);

                        // #2/#3 — best-effort NEW-registration detection.
                        // Keycloak returns createdTimestamp (epoch ms); treat an
                        // account created within the last 5 minutes as a new sign-up.
                        const createdAt = (profile as { createdTimestamp?: number }).createdTimestamp;
                        const isNewRegistration =
                            typeof createdAt === "number" && Date.now() - createdAt < 5 * 60 * 1000;
                        if (isNewRegistration) {
                            Analytics.registerSuccess(resolveRegisterMethod(kc));
                        }
                    } catch (error) {
                        console.error('Failed to load user profile:', error);
                        updateRecoilState("userProfileStateAtom", null);
                        return;
                    }
                } else {
                    // Use the cached profile
                    user = userProfile;
                }
                setSession({
                    token: currentToken,
                    idTokenHint: currentIdTokenHint,
                    refreshToken: currentRefreshToken,
                    isAnonymous: false,
                    userProfile: user,
                });

                // Attach non-PII identity to analytics (fire-and-forget).
                void sha256(user.id).then((hashed) => Analytics.setUser(hashed));

                emitTokenReadyEvent();
                await saveRefreshTokenToCookie(currentRefreshToken);
                updateRecoilState("tokenStateAtom", currentToken);
                updateRecoilState("isAuthenticatedStateAtom", true);
                updateRecoilState("isAnonymousStateAtom", false);

                // Load and set user profile
                try {
                    // Handle redirect after successful login
                    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
                    if (redirectPath) {
                        // Clear the stored path before redirecting
                        sessionStorage.removeItem('redirectAfterLogin');
                        const pendingBookingData = sessionStorage.getItem('pendingBookingData');
                        // Only redirect if we're not already on the target page
                        if (pendingBookingData && redirectPath === '/checkout') {
                            // Let the checkout page handle the state restoration
                            window.location.href = redirectPath;
                            // setTimeout(() => {
                            //     _eventBus.emit({
                            //         eventName: "ROUTE_CHANGE",
                            //         data: "/checkout"
                            //     })
                            // }, 200)

                        } else if (window.location.pathname !== redirectPath) {
                            window.location.href = redirectPath;
                        }
                    } else if (window.location.pathname === '/') {
                        // Only redirect to search if we're on the home page.
                        // Use soft navigation to avoid a full page reload that
                        // restarts all flows and causes duplicate API calls.
                        _eventBus.emit({
                            eventName: "ROUTE_CHANGE",
                            data: "/search",
                        });
                    }

                } catch (error) {
                    console.error('Failed to load user profile:', error);
                    updateRecoilState("userProfileStateAtom", null);
                }

                // Clean the URL by removing the hash fragment
                if (window.location.hash) {
                    const cleanURL = window.location.href.split('#')[0];
                    window.history.replaceState({}, document.title, cleanURL);
                }
            }
        },
        [userProfile]
    );

    // Initialize Keycloak and manage authentication state
    useEffect(() => {
        const initializeAuth = async () => {
            if (isInitializingRef.current) {
                console.log("Auth initialization already in progress, skipping.");
                return;
            }
            console.log("[useAuth] Starting auth initialization...");
            isInitializingRef.current = true;

            try {
                console.log("[useAuth] Running auth initialization logic...");
                const config = await getRuntimeConfig();
                // Allow the mobile app's payment deep-link (/checkout/payment?sessionId=...)
                // to bypass mandatory login — the page only drives the Mastercard checkout
                // session and does not require a backend token.
                const isPaymentDeepLink =
                    typeof window !== "undefined" &&
                    window.location.pathname === "/checkout/payment" &&
                    !!new URLSearchParams(window.location.search).get("sessionId");
                const loginRequired =
                    config?.NEXT_PUBLIC_LOGIN_REQUIRED === "true" && !isPaymentDeepLink;
                const session = await getSession();
                let hasAuthenticatedSession = false;
                if (session?.token && !session.isAnonymous) {
                    console.log("[useAuth] Found existing authenticated session.");
                    hasAuthenticatedSession = true;
                    emitTokenReadyEvent();
                    updateRecoilState("isAuthenticatedStateAtom", true);
                    updateRecoilState("isAnonymousStateAtom", false);
                    updateRecoilState("tokenStateAtom", session.token);
                    if (session.userProfile) {
                        updateRecoilState("userProfileStateAtom", session.userProfile);
                    }
                    updateRecoilState("authLoadedStateAtom", true);
                    apiConfigManager.setUserLoginStatus(true);
                } else if (loginRequired) {
                    // Skip the anonymous-token fetch AND the authLoadedStateAtom flip
                    // — flipping it would let the app render in guest mode
                    // (isAnonymousStateAtom defaults to true) while we wait for
                    // Keycloak. The kc.init() call below uses onLoad:"login-required"
                    // when this flag is on, which makes Keycloak redirect the
                    // browser to its login page immediately if no session exists.
                    console.log("[useAuth] Login required flag is on; deferring to kc.init() with login-required.");
                } else {
                    // Guests do not use a token. The Authorization/requestor headers
                    // (and the requestor query param) are not sent for guests — see
                    // ApiRequest/buildHeaders — so we simply mark the session as an
                    // anonymous guest.
                    console.log("[useAuth] No authenticated session found — entering guest mode (no token fetched).");

                    // Unblock token-gated API calls immediately: guests never send
                    // Authorization headers, so there is nothing to wait for. Without
                    // this, ApiRequest's TOKEN_READY queue holds all metadata calls
                    // (stops, ticket-options, passenger-types) behind kc.init()'s
                    // silent check-sso (~2s of Keycloak iframes). If check-sso later
                    // finds an SSO session, the normal login path still runs and the
                    // guest responses are user-independent anyway.
                    emitTokenReadyEvent();

                    // Load any previously cached API config from storage. Without a
                    // token fetchApiConfig short-circuits to [] (no network call), so
                    // this only rehydrates a cached config when one is present.
                    const isApiConfigCached = apiConfigManager.isConfigCached();
                    if (!isApiConfigCached) {
                        setTimeout(async () => {
                            await apiConfigManager.fetchApiConfig(true)
                        })
                    }

                    setSession({ token: "", isAnonymous: true, idTokenHint: "" });
                    updateRecoilState("isAuthenticatedStateAtom", false);
                    updateRecoilState("isAnonymousStateAtom", true);
                    updateRecoilState("tokenStateAtom", "");
                    updateRecoilState("authLoadedStateAtom", true);
                }
                // Initialize Keycloak
                const kc = new Keycloak({
                    url: config.NEXT_PUBLIC_KEYCLOAK_URL,
                    realm: config.NEXT_PUBLIC_KEYCLOAK_REALM ?? '',
                    clientId: config.NEXT_PUBLIC_KEYCLOAK_CLIENTID ?? '',
                });

                keycloakRef.current = kc;

                kc.onTokenExpired = () => {
                    console.log("[useAuth] Keycloak token expired, attempting refresh...");
                    kc.updateToken(30)
                        .then((refreshed: boolean) => {
                            if (refreshed && kc.token) {
                                setSession({
                                    token: kc.token!,
                                    refreshToken: kc.refreshToken!,
                                    isAnonymous: false,
                                    idTokenHint: kc.idToken!,
                                });
                                updateRecoilState("tokenStateAtom", kc.token!);
                                console.log('[useAuth] Token refreshed');
                            } else {
                                console.warn('[useAuth] Token not refreshed, needs re-login');
                                logout();
                            }
                        })
                        .catch(() => {
                            console.error('[useAuth] Failed to refresh token');
                            logout();
                        });
                };

                kc.onAuthSuccess = () => {
                    console.log('[useAuth] Authentication success');

                    const url = new URL(window.location.href);
                    const searchParams = new URLSearchParams(url.search);
                    if (searchParams.get("successLogin") === "true") {
                        apiConfigManager.resetConfig();
                        searchParams.delete("successLogin");
                        const newUrl = `${url.origin}${url.pathname}?${searchParams}`;
                        window.history.replaceState(null, '', newUrl);
                    }
                    //updateSessionAfterLogin(kc);
                };

                kc.onAuthRefreshSuccess = () => {
                    console.log('[useAuth] Authentication token refreshed');
                    updateSessionAfterLogin(kc);
                };

                kc.onAuthError = (error: any) => {
                    console.error('[useAuth] Authentication error:', error);
                    logout();
                };

                kc.onAuthLogout = () => {
                    console.log('[useAuth] User logged out via Keycloak');
                    // logout() handles all cleanup and redirect; do not
                    // manually set window.location here or it races with
                    // the Keycloak redirect inside logout().
                    logout();
                };

                console.log("[useAuth] Initializing Keycloak (kc.init)...");
                // When loginRequired is on and we have no local session, use
                // onLoad:"login-required" — Keycloak will redirect the browser
                // to its login page immediately (no app render in guest mode).
                // Otherwise use check-sso to silently detect an existing
                // Keycloak session via the hidden silent-check-sso.html iframe;
                // silentCheckSsoFallback is disabled so the adapter never
                // redirects the main page when the iframe check fails.
                const useLoginRequired = loginRequired && !hasAuthenticatedSession;
                const authenticated = await kc.init({
                    onLoad: useLoginRequired ? "login-required" : "check-sso",
                    silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
                    silentCheckSsoFallback: false,
                    checkLoginIframe: false,
                    pkceMethod: 'S256',
                    flow: 'standard',
                    redirectUri: window.location.href,
                });
                console.log(`[useAuth] Keycloak initialized. Authenticated: ${authenticated}`);

                if (authenticated) {
                    console.log("[useAuth] User is authenticated via Keycloak, updating session...");
                    await updateSessionAfterLogin(kc);
                    // Ensure authLoaded is flipped on the post-callback path
                    // (the early `else if (loginRequired)` branch no longer sets it).
                    updateRecoilState("authLoadedStateAtom", true);
                    console.log("[useAuth] Session updated after Keycloak login.");
                } else if (loginRequired && !hasAuthenticatedSession) {
                    console.log('[useAuth] Login required and not authenticated — redirecting to Keycloak.');
                    kc.login({ redirectUri: window.location.href }).catch((error: any) => {
                        console.error('[useAuth] kc.login() failed:', error);
                    });
                    // Browser will navigate away; do not flip isAuthCheckCompleteStateAtom
                    // (which would let the rest of the app render anonymous-state UI for a frame).
                    return;
                } else {
                    console.log('[useAuth] User is not authenticated via Keycloak.');
                    emitTokenReadyEvent();
                }
                updateRecoilState("isAuthCheckCompleteStateAtom", true);
            } catch (error) {
                console.error('[useAuth] Error initializing authentication:', error);
                // Set auth loaded even if there was an error
                updateRecoilState("authLoadedStateAtom", true);
            } finally {
                console.log("[useAuth] Auth initialization finished.");
                updateRecoilState("initializedStateAtom", true);
                isInitializingRef.current = false;
            }
        };
        console.log("[useAuth] useEffect triggered, calling initializeAuth.");
        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for logout events from actions
    useEffect(() => {
        const unsubscribe = _eventBus.on({
            eventName: 'USER_LOGOUT',
            handler: () => {
                logout();
            }
        });

        return () => {
            unsubscribe.off();
        };
    }, [logout]);

    return {
        initialized,
        isAuthCheckComplete,
        isAuthenticated,
        isAnonymous,
        token,
        login,
        logout,
        authLoaded,
    };
};